
class KiiveAudioWidget {
  constructor(root) {
    this.root = root;

    this.video = root.querySelector("[data-kiive-aw-video]");
    this.videoContainer = root.querySelector("[data-kiive-aw-video-container]");
    this.playBtn = root.querySelector("[data-kiive-aw-play]");
    this.switchBtns = root.querySelectorAll("[data-kiive-aw-switch]");
    this.trackListEl = root.querySelector("[data-kiive-aw-tracklist]");
    this.titleEl = root.querySelector("[data-kiive-aw-title]");
    this.prevBtn = root.querySelector("[data-kiive-aw-prev]");
    this.nextBtn = root.querySelector("[data-kiive-aw-next]");
    this.loading = root.querySelector("[data-kiive-aw-loading]");
    this.progressBar = root.querySelector("[data-kiive-aw-progress]");

    this.data = JSON.parse(root.querySelector("[data-kiive-aw-data]").textContent || "[]");
    this.currentIndex = 0;

    this.state = "bypass"; // 'processed' or 'bypass'
    this.isPlaying = false;

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContext();
    if (this.isMobile()) {
      this.audioContext = new AudioContext({ latencyHint: "playback", sampleRate: 44100 });
    }

    // Cache decoded buffers: key = `${index}:${state}`
    this.bufferCache = new Map();

    // Currently playing buffer source
    this.currentSource = null;
    this.startTime = 0;
    this.pausedTime = 0;

    this.bindUI();
    this.renderTrackList();
    this.loadTrack(this.currentIndex, { warm: true }).catch(console.error);
  }

  isMobile() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  async ensureAudioContext() {
    if (this.audioContext.state === "suspended") await this.audioContext.resume();
  }

  showLoading(on) {
    if (!this.loading) return;
    this.loading.style.display = on ? "flex" : "none";
    if (this.progressBar) this.progressBar.style.width = on ? "15%" : "0%";
  }

  setProgress(pct) {
    if (this.progressBar) this.progressBar.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  }

  bindUI() {
    this.playBtn?.addEventListener("click", async () => {
      if (this.isPlaying) {
        this.stop();
      } else {
        await this.play();
      }
    });

    this.switchBtns.forEach(btn => {
      btn.addEventListener("click", async () => {
        const nextState = btn.getAttribute("data-kiive-aw-switch");
        if (!nextState) return;

        this.switchBtns.forEach(b => b.classList.remove("is-active"));
        btn.classList.add("is-active");

        this.state = nextState;

        // Video reveal (CSS-driven)
        // processed (On) should show the BOTTOM half, which requires the -50% shift class
        if (this.state === "processed") {
        this.videoContainer?.classList.add("is-bypassed");
      } else {
        this.videoContainer?.classList.remove("is-bypassed");
}

        // Audio A/B (keeps playhead position)
        await this.switchAudioState(this.state);
      });
    });

    // Track click (desktop list)
    this.root.addEventListener("click", async (e) => {
      const btn = e.target.closest("[data-kiive-aw-track]");
      if (!btn) return;
      const idx = Number(btn.getAttribute("data-kiive-aw-track"));
      if (Number.isNaN(idx)) return;
      await this.switchTrack(idx, { autoplay: true });
    });

    // Mobile prev/next
    this.prevBtn?.addEventListener("click", async () => {
      const idx = (this.currentIndex - 1 + this.data.length) % this.data.length;
      await this.switchTrack(idx, { autoplay: true });
    });

    this.nextBtn?.addEventListener("click", async () => {
      const idx = (this.currentIndex + 1) % this.data.length;
      await this.switchTrack(idx, { autoplay: true });
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.isPlaying) this.stop();
    });
  }

  renderTrackList() {
    if (!this.trackListEl) return;
    this.trackListEl.innerHTML = this.data.map((t, i) => {
      return `
        <div class="kiive-aw__track">
          <button type="button" data-kiive-aw-track="${i}" class="${i === this.currentIndex ? "is-active" : ""}">
            ${this.escapeHTML(t.title || `Track ${i+1}`)}
          </button>
        </div>
      `;
    }).join("");
  }

  setActiveTrackButton() {
    if (!this.trackListEl) return;
    this.trackListEl.querySelectorAll("button[data-kiive-aw-track]").forEach((b) => {
      const idx = Number(b.getAttribute("data-kiive-aw-track"));
      b.classList.toggle("is-active", idx === this.currentIndex);
    });
  }

  escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, (m) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;"
    }[m]));
  }

  async fetchAndDecode(url) {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    const buf = await res.arrayBuffer();
    return await this.audioContext.decodeAudioData(buf);
  }

  cacheKey(index, state) {
    return `${index}:${state}`;
  }

  // Lazy-load buffer for a track+state
  async getBuffer(index, state) {
    const key = this.cacheKey(index, state);
    if (this.bufferCache.has(key)) return this.bufferCache.get(key);

    const track = this.data[index];
    const url = state === "processed" ? track.on : track.off;
    if (!url) throw new Error(`Missing audio url for ${state} on track ${index}`);

    // progress animation
    this.setProgress(35);
    const decoded = await this.fetchAndDecode(url);
    this.setProgress(70);

    this.bufferCache.set(key, decoded);
    return decoded;
  }

  createSource(buffer) {
    const src = this.audioContext.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(this.audioContext.destination);
    return src;
  }

  async loadTrack(index, { warm = false } = {}) {
    const track = this.data[index];
    if (!track) return;

    // title
    if (this.titleEl) this.titleEl.textContent = track.title || "";

    // video
    if (this.video) {
      // Only set src if not already set or changed
      if (this.video.getAttribute("src") !== track.video) {
        this.video.pause();
        this.video.setAttribute("src", track.video || "");
        this.video.currentTime = 0.001;
        this.video.muted = true;
        this.video.loop = true;
        this.video.playsInline = true;
        // preload metadata only (lighter)
        this.video.preload = warm ? "metadata" : "none";
        this.video.load();
        // Ensure the video matches current UI state on load
       if (this.state === "processed") {
       this.videoContainer?.classList.add("is-bypassed");   // show bottom half (ON)
      } else {
       this.videoContainer?.classList.remove("is-bypassed"); // show top half (BYPASS)
}
      }
    }

    // If warm=true, we pre-decode the initial state in background (small quality-of-life)
    if (warm) {
      try {
        this.showLoading(true);
        // preload current state only
        await this.getBuffer(index, this.state);
      } finally {
        this.setProgress(100);
        setTimeout(() => this.showLoading(false), 120);
      }
    }
  }

  async switchTrack(index, { autoplay = false } = {}) {
    if (index === this.currentIndex) return;
    if (!this.data[index]) return;

    // stop current playback cleanly
    if (this.isPlaying) this.stop();

    this.showLoading(true);
    this.setProgress(10);

    this.currentIndex = index;
    this.setActiveTrackButton();

    // clear paused times
    this.startTime = 0;
    this.pausedTime = 0;

    // load video + warm current state buffer
    try {
      await this.loadTrack(index, { warm: true });
      this.setProgress(85);
    } catch (e) {
      console.error(e);
    } finally {
      this.setProgress(100);
      setTimeout(() => this.showLoading(false), 150);
    }

    if (autoplay) {
      await new Promise(r => setTimeout(r, 200));
      await this.play();
    }
  }

  async play() {
    await this.ensureAudioContext();

    const index = this.currentIndex;

    // Load current state buffer on demand
    this.showLoading(true);
    this.setProgress(20);

    let buffer;
    try {
      buffer = await this.getBuffer(index, this.state);
      this.setProgress(70);
    } finally {
      this.setProgress(100);
      setTimeout(() => this.showLoading(false), 120);
    }

    // Start video
    if (this.video) {
      this.video.currentTime = 0.001;
      try { await this.video.play(); } catch (_) {}
    }

    // Start audio source
    this.currentSource?.stop?.();
    this.currentSource?.disconnect?.();

    this.currentSource = this.createSource(buffer);

    // start from pausedTime if any
    this.startTime = this.audioContext.currentTime - (this.pausedTime || 0);
    this.currentSource.start(0, (this.pausedTime || 0) % buffer.duration);

    this.isPlaying = true;
    this.playBtn?.classList.add("is-playing");

    // Prefetch the other state in the background for instant A/B (optional)
    const other = this.state === "processed" ? "bypass" : "processed";
    this.getBuffer(index, other).catch(() => {});
  }

  stop() {
    try {
      if (this.currentSource) {
        this.currentSource.stop();
        this.currentSource.disconnect();
      }
    } catch (_) {}

    this.currentSource = null;

    // Record pausedTime from AudioContext timing
    if (this.isPlaying) {
      this.pausedTime = Math.max(0, this.audioContext.currentTime - this.startTime);
    } else {
      this.pausedTime = 0;
    }

    if (this.video) {
      this.video.currentTime = 0.001;
      requestAnimationFrame(() => this.video.pause());
    }

    this.isPlaying = false;
    this.playBtn?.classList.remove("is-playing");
  }

  async switchAudioState(nextState) {
    if (!this.data[this.currentIndex]) return;

    // If not playing, just warm the buffer so first play is fast
    if (!this.isPlaying) {
      this.getBuffer(this.currentIndex, nextState).catch(() => {});
      return;
    }

    await this.ensureAudioContext();

    const currentTime = this.audioContext.currentTime - this.startTime;
    const buffer = await this.getBuffer(this.currentIndex, nextState);

    // swap buffer sources at same playhead position
    try {
      this.currentSource?.stop();
      this.currentSource?.disconnect();
    } catch (_) {}

    this.currentSource = this.createSource(buffer);
    this.currentSource.start(0, (currentTime % buffer.duration));
    this.startTime = this.audioContext.currentTime - currentTime;
  }
}

function bootKiiveAudioWidgets() {
  document.querySelectorAll("[data-kiive-audiowidget]").forEach((root) => {
    try { new KiiveAudioWidget(root); } catch (e) { console.error(e); }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootKiiveAudioWidgets);
} else {
  bootKiiveAudioWidgets();
}